import { Logo } from '@/components/Logo';
import { useAuthStore } from '@/stores/useAuthStore';
import i18n from '@/translations/config';
import { cn } from '@/utils/cn';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SidebarFooter } from './SidebarFooter';
import { SidebarHeader } from './SidebarHeader';
import { SidebarLink } from './SidebarLink';

export const Sidebar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const lang = i18n.language;
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sidebarPersonnalLinks = [
    {
      to: '/dashboard',
      icon: '🪪',
      label: t('sidebar:cards'),
      role: ['admin', 'manager', 'user'],
    },
    {
      to: '/dashboard/contacts',
      icon: '📒',
      label: t('sidebar:contacts'),
      role: ['admin', 'manager', 'user'],
      id: 'contacts-nav-link',
    },
    {
      to: '/dashboard/events',
      icon: '📅',
      label: t('sidebar:events'),
      role: ['admin', 'manager', 'user'],
      id: 'events-nav-link',
      notificationCount:
        user?.meeting_requests && user?.meeting_requests > 0
          ? user?.meeting_requests
          : undefined,
    },
    {
      to: 'https://linko-connect.com/contact/',
      icon: '🙋',
      label: t('sidebar:support'),
      role: ['admin', 'manager', 'user'],
      isExternal: true,
    },
  ];

  const sidebarManagementLinks = [
    {
      to: '/dashboard/companies',
      icon: '🏢',
      label: t('sidebar:companies'),
      role: ['admin'],
    },
    {
      to: '/dashboard/my-company',
      icon: '🏢',
      label: t('sidebar:myCompany'),
      role: ['manager'],
    },
    {
      to: '/dashboard/users',
      icon: '👥',
      label: t('sidebar:users'),
      role: ['admin', 'manager'],
    },
    {
      to: '/dashboard/events/events-management',
      icon: '📅',
      label: t('sidebar:events'),
      role: ['admin'],
    },
    {
      to: '/dashboard/qrcode',
      icon: '📱',
      label: t('sidebar:qrcode') || 'Générateur QR Code',
      role: ['admin'],
    },
    {
      to: '/dashboard/statistics',
      icon: '📊',
      label: t('sidebar:statistics'),
      role: ['admin'],
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isModalOpen) return;
      if ((event.target as HTMLElement)?.closest('.dropdown-menu')) return;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsMobileOpen(false);
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileOpen, isModalOpen]);

  const toggleMobileSidebar = () => setIsMobileOpen(prev => !prev);
  const handleLinkClick = () => {
    window.innerWidth < 1024 && setIsMobileOpen(false);
  };

  return (
    <div className="relative">
      <SidebarHeader toggleSidebar={toggleMobileSidebar} />
      <aside
        ref={sidebarRef}
        className={cn(
          'flex flex-col border-r border-gray-200 bg-white',
          'fixed left-0 top-0 z-30 lg:static',
          'w-60 transition-all duration-300 ease-in-out lg:w-60 lg:translate-x-0',
          'h-dvh lg:h-lvh',
          {
            'translate-x-0': isMobileOpen,
            '-translate-x-full': !isMobileOpen,
          },
        )}
      >
        {/* Logo sobre */}
        <Link
          className="my-6 flex items-center justify-center"
          to={`/${i18n.language}/dashboard`}
        >
          <Logo className="h-8 w-auto" />
        </Link>

        {/* Contenu scrollable */}
        <div className="flex grow flex-col overflow-y-auto px-3">
          {/* Section Navigation Personnelle */}
          <div className="mb-2">
            <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('sidebar:personal') || 'Personnel'}
            </h3>
            <nav className="space-y-1">
              {sidebarPersonnalLinks.map(
                ({
                  to,
                  icon,
                  label,
                  role,
                  isExternal,
                  notificationCount,
                  id,
                }) =>
                  user &&
                  role.includes(user.role) && (
                    <SidebarLink
                      key={to}
                      to={isExternal ? to : `/${lang}${to}`}
                      onClick={handleLinkClick}
                      setSidebarIsOpen={setIsMobileOpen}
                      isExternal={isExternal}
                      notificationCount={notificationCount}
                      id={id}
                    >
                      <span className="flex items-center justify-center text-[22px] text-gray-700">
                        {icon}
                      </span>
                      <span className="ml-3 truncate font-semibold text-gray-800">
                        {label}
                      </span>
                    </SidebarLink>
                  ),
              )}
            </nav>
          </div>

          {/* Séparateur sobre */}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <div className="my-2 border-b border-gray-200" />
          )}

          {/* Section Management */}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <div className="mb-2">
              <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {t('sidebar:management') || 'Gestion'}
              </h3>
              <nav className="space-y-1">
                {sidebarManagementLinks.map(
                  ({ to, icon, label, role }) =>
                    user &&
                    role.includes(user.role) && (
                      <SidebarLink
                        key={to}
                        to={`/${lang}${to}`}
                        onClick={handleLinkClick}
                        setSidebarIsOpen={setIsMobileOpen}
                      >
                        <span className="flex items-center justify-center text-[22px] text-gray-700">
                          {icon}
                        </span>
                        <span className="ml-3 truncate font-semibold text-gray-800">
                          {label}
                        </span>
                      </SidebarLink>
                    ),
                )}
              </nav>
            </div>
          )}
        </div>

        {/* Footer sobre */}
        <div className="border-t border-gray-200 bg-white p-4">
          <SidebarFooter
            setIsModalOpen={setIsModalOpen}
            setSidebarIsOpen={setIsMobileOpen}
          />
        </div>
      </aside>

      {/* Overlay mobile foncé */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 transition-all duration-300 lg:hidden"
          onClick={() => !isModalOpen && toggleMobileSidebar()}
        ></div>
      )}
    </div>
  );
};
